/**
 * model/forces.ts — Derivazione pura delle frecce dei carichi applicati
 * (28-09-2026, richiesta utente: "aggiungere le forze applicate sul
 * connettore simile a [immagine di riferimento], fup fdown flat fax").
 *
 * SSOT dei valori: `LockCalculatorFormValues.loads` (form del calcolatore —
 * vertical/uplift/axial/lateral in kN, gia' mostrati come numeri in
 * ResultsSection.tsx). Questo modulo NON calcola nulla di normativo: prende
 * i 4 valori gia' inseriti dall'utente e decide SOLO punto/direzione/
 * etichetta della freccia — stesso confine di `model/quotes.ts` (SPEC §6:
 * model/ puro, three.js solo per l'algebra vettoriale, zero side-effect).
 *
 * NON filtrare per "incluso nella verifica combinata" (28-09-2026, bug
 * corretto su segnalazione utente: "le forze non devono essere vincolate al
 * pulsante di combined load verification, non c'entra nulla" — quel flag
 * decide quali assi ENTRANO nella formula di interazione normativa, non se
 * un carico esiste fisicamente sul connettore. Un carico si mostra se e solo
 * se ha un valore reale (non null/zero), indipendentemente da quel flag.
 *
 * ANCORAGGIO (corretto 28-09-2026, richiesta utente: "sarebbe meglio
 * aggiungere un piano che si trovi sempre a meta' dell'assieme lock, e su
 * questo devono essere posizionate le forze"): NON `scene.plane.origin` —
 * quel punto e' il riferimento del piano connettore (X=0, Z=0 gia' centrati
 * per costruzione, ma Y e' l'ALTEZZA DI RIFERIMENTO del piano, es. il top
 * trave, non il centro verticale reale dell'assieme). Il vero centro si
 * ottiene mediando le posizioni MONDO GIA' RISOLTE di tutti i connettori
 * (`computeAssemblyMidpoint` sotto) — nessuna ri-derivazione della formula di
 * flush: X e Z tornano 0 per costruzione (joist/header simmetrici sul gap,
 * connettori multipli simmetrici su Z — `nConnectorsOffsetsZ`), Y e' la
 * media reale fra centro joist e centro header (e fra piu' coppie, se
 * presenti) — il vero "centro dell'assieme lock".
 *
 * DIREZIONI (schematiche, non un diagramma di corpo libero FEA-accurato — i
 * carichi sono valori GLOBALI per l'intero assieme, non per singolo
 * connettore/lato, quindi non esiste un "asse fibra" unico da seguire quando
 * l'orientamento header/joist o lo skew alphaDeg/betaDeg cambiano): verticale
 * = mondo Y (su/giu', mai ambiguo), assiale = mondo X (normale del piano
 * connettore, la direzione che separa joist e header), laterale = mondo Z
 * (l'asse in-piano rimanente, perpendicolare sia alla verticale sia alla
 * normale).*/

import type { Vec3 } from "../data/types";
import { roundTo } from "../utils/math";

/**
 * Centro reale dell'assieme lock: media delle posizioni MONDO gia' risolte
 * di tutti i connettori (joist+header, tutte le coppie) — non una nuova
 * derivazione geometrica, solo una media di dati gia' fidati. Nessun
 * connettore (form vuoto) → origine.
 * @param connectorPositions `scene.connectors.map(c => c.position)`.*/
export function computeAssemblyMidpoint(connectorPositions: readonly Vec3[]): Vec3 {
  if (connectorPositions.length === 0) return [0, 0, 0];
  const sum = connectorPositions.reduce<Vec3>(
    (acc, p) => [acc[0] + p[0], acc[1] + p[1], acc[2] + p[2]],
    [0, 0, 0],
  );
  const n = connectorPositions.length;
  return [sum[0] / n, sum[1] / n, sum[2] / n];
}

// ─── Contratti con il form (SSOT: model/form.ts) ─────────────────

/** Carichi applicati (kN, nullable — non ancora inseriti dall'utente).*/
export interface ForceLoads {
  vertical: number | null;
  uplift: number | null;
  axial: number | null;
  lateral: number | null;
}

/** Una freccia di carico gia' risolta (due punti mondo + etichetta).*/
export interface ForceArrowSpec {
  id: "vertical" | "uplift" | "axial" | "lateral";
  /** Punto di applicazione (coda della freccia, sul piano connettore).*/
  from: Vec3;
  /** Punta della freccia (from + direzione*lunghezza) — SOLO geometria dell'asta, non dell'etichetta.*/
  to: Vec3;
  /**
   * Posizione dell'etichetta, PRIMA della punta (28-09-2026, bug corretto su
   * segnalazione utente: l'etichetta a `to` copriva la punta della freccia).
   * Calcolata qui (non nel render) per restare nel layer model/ puro.*/
  labelPosition: Vec3;
  /** Etichetta gia' formattata, stessi simboli di ResultsSection.tsx (es. "↕ 12.3 kN").*/
  label: string;
}

// ─── Direzioni schematiche (world, unitarie) — vedi doc-comment in cima ──

const DIRECTIONS: Record<ForceArrowSpec["id"], Vec3> = {
  vertical: [0, -1, 0],
  uplift: [0, 1, 0],
  axial: [1, 0, 0],
  lateral: [0, 0, 1],
};

/** Stessi simboli di `hooks/useLockCalculatorResults.ts` (assi verifica combinata) — SSOT unica.*/
const SYMBOLS: Record<ForceArrowSpec["id"], string> = {
  vertical: "↕",
  uplift: "⇡",
  axial: "↔",
  lateral: "⇆",
};

/**
 * Frazione dell'asta a cui sta l'etichetta (0=coda, 1=punta) — PRIMA della
 * cono di terminazione (`FORCE_ARROW_DEFAULTS.headLengthMm`), altrimenti la
 * copre. Non esposta in config/defaults.ts: e' un dettaglio di layout di
 * QUESTO modulo, non un parametro di dominio.*/
const LABEL_POSITION_FRACTION = 0.7;

function addScaled(origin: Vec3, dir: Vec3, scale: number): Vec3 {
  return [origin[0] + dir[0] * scale, origin[1] + dir[1] * scale, origin[2] + dir[2] * scale];
}

/**
 * Deriva le frecce dei carichi attivi. Un asse e' mostrato se e solo se ha
 * un valore reale (non null/zero) — NON e' filtrato dal flag "incluso nella
 * verifica combinata" (vedi doc-comment in cima al file).
 *
 * @param origin Punto di ancoraggio — SEMPRE `computeAssemblyMidpoint(...)`
 * (il centro reale dell'assieme, non un singolo connettore: i carichi sono
 * globali per l'assieme).
 * @param loads Carichi dal form (kN).
 * @param shaftLengthMm Lunghezza fissa della freccia (`FORCE_ARROW_DEFAULTS.shaftLengthMm`).
 * @returns Lista di frecce da disegnare (puo' essere vuota).*/
export function deriveForceArrows(origin: Vec3, loads: ForceLoads, shaftLengthMm: number): ForceArrowSpec[] {
  const axes: Array<{ id: ForceArrowSpec["id"]; value: number | null }> = [
    { id: "vertical", value: loads.vertical },
    { id: "uplift", value: loads.uplift },
    { id: "axial", value: loads.axial },
    { id: "lateral", value: loads.lateral },
  ];

  const arrows: ForceArrowSpec[] = [];
  for (const axis of axes) {
    if (axis.value === null || axis.value === 0) continue;
    const direction = DIRECTIONS[axis.id];
    arrows.push({
      id: axis.id,
      from: origin,
      to: addScaled(origin, direction, shaftLengthMm),
      labelPosition: addScaled(origin, direction, shaftLengthMm * LABEL_POSITION_FRACTION),
      label: `${SYMBOLS[axis.id]} ${roundTo(axis.value, 1)} kN`,
    });
  }
  return arrows;
}
